import React from 'react';
import { TransferPlayer } from '../../domain/entities';
import { Modal } from '../shared/Modal';
import { ASSISTANT_MANAGER_LEVELS } from '../../domain/constants';
import './PlayerDetailModals.css';

const negotiationOptions = [
    { label: '-20%', multiplier: 0.8 }, { label: '-10%', multiplier: 0.9 },
    { label: 'As Is', multiplier: 1.0 }, { label: '+10%', multiplier: 1.1 },
    { label: '+20%', multiplier: 1.2 }
];

const generateTransferReview = (player: TransferPlayer, selectedOfferMultiplier: number, assistantManagerLevel: number = 1): string => {
    const offerAmount = Math.round(player.askingPrice * selectedOfferMultiplier);
    const valuePerSkill = offerAmount / player.skill;
    const skillLevel = player.skill > 75 ? 'elite' : player.skill > 60 ? 'strong' : player.skill > 45 ? 'average' : 'below-average';
    const ageCategory = player.age < 25 ? 'young prospect' : player.age < 30 ? 'peak years' : player.age < 33 ? 'experienced' : 'aging';
    const potentialValue = player.potential > player.skill ? player.potential - player.skill : 0;
    const fitnessStatus = player.fitness > 75 ? 'excellent condition' : player.fitness > 50 ? 'decent shape' : 'poor condition';

    let review = '';

    // Get the current assistant manager's accuracy (affects confidence in recommendations)
    const amLevel = ASSISTANT_MANAGER_LEVELS[assistantManagerLevel - 1] || ASSISTANT_MANAGER_LEVELS[0];
    const accuracy = amLevel.accuracy;

    // Add confidence qualifier based on assistant manager level
    const confidenceQualifier = accuracy >= 0.95 ? '' :
                                 accuracy >= 0.9 ? 'I believe ' :
                                 accuracy >= 0.8 ? 'I think ' :
                                 accuracy >= 0.7 ? 'In my assessment, ' :
                                 'From what I can tell, ';

    // Opening statement
    if (selectedOfferMultiplier < 0.9) {
        review += `${confidenceQualifier}we're in a strong negotiating position. We're offering well below market value. `;
    } else if (selectedOfferMultiplier < 1.0) {
        review += `${confidenceQualifier}this is a reasonable offer below asking price. `;
    } else if (selectedOfferMultiplier === 1.0) {
        review += `${confidenceQualifier}we're matching the asking price exactly. `;
    } else if (selectedOfferMultiplier < 1.2) {
        review += `${confidenceQualifier}this is an aggressive offer above asking price. `;
    } else {
        review += `${confidenceQualifier}we're making a premium offer well above asking price. `;
    }

    // Value assessment
    if (valuePerSkill < 1000) {
        review += `Exceptional value per unit of skill - this is a bargain. `;
    } else if (valuePerSkill < 1500) {
        review += `Good value for this caliber of player. `;
    } else if (valuePerSkill < 2000) {
        review += `Fair market value. `;
    } else if (valuePerSkill < 2500) {
        review += `Premium pricing for a ${skillLevel} player. `;
    } else {
        review += `Expensive for what we're getting. `;
    }

    // Potential assessment
    if (potentialValue > 5) {
        review += `Room for development - could improve further. `;
    } else if (potentialValue > 0) {
        review += `Some potential to grow. `;
    } else if (player.potential >= player.skill) {
        review += `Peak ability already reached. `;
    } else {
        review += `May be declining in his prime. `;
    }

    // Age and fitness assessment
    review += `At ${player.age}, he's in his ${ageCategory}. `;

    if (player.fitness < 50) {
        review += `However, currently in ${fitnessStatus} - may need time to fully integrate. `;
    } else if (player.fitness < 75) {
        review += `Currently in ${fitnessStatus}. `;
    } else {
        review += `Currently in ${fitnessStatus} - ready to contribute immediately. `;
    }

    // Final recommendation - weighted by experience level
    let verdict = '';
    if (selectedOfferMultiplier < 0.95 && valuePerSkill < 1500) {
        verdict = accuracy >= 0.85 ? 'Strong opportunity - pursue aggressively.' : 'Decent opportunity - worth pursuing.';
    } else if (selectedOfferMultiplier < 1.15 && (valuePerSkill < 2000 || potentialValue > 3)) {
        verdict = accuracy >= 0.85 ? 'Good signing at this price - recommended.' : 'Reasonable signing - could work.';
    } else if (selectedOfferMultiplier > 1.2 && valuePerSkill > 2000) {
        verdict = accuracy >= 0.85 ? 'Overpriced - consider negotiating down.' : 'Quite expensive - think about negotiating.';
    } else if (player.injury?.type === 'Severe') {
        verdict = accuracy >= 0.85 ? 'Severe injury present - exercise caution.' : 'Injury concerns - be careful.';
    } else {
        verdict = accuracy >= 0.85 ? 'Reasonable transfer, proceed with care.' : 'Seems okay - proceed cautiously.';
    }

    review += `Verdict: ${verdict}`;

    return review;
};

interface NegotiationModalProps {
    player: TransferPlayer | null;
    onClose: () => void;
    onNegotiate: () => void;
    onFinalize: () => void;
    step: 'initial' | 'player_terms' | 'complete';
    message: { type: 'success' | 'error', text: string } | null;
    isSubmitting: boolean;
    selectedOfferMultiplier: number;
    onOfferMultiplierChange: (multiplier: number) => void;
    selectedWageMultiplier: number;
    onWageMultiplierChange: (multiplier: number) => void;
    assistantManagerLevel?: number;
    blockStatus?: { blocked: boolean; weeksRemaining: number };
}

export const NegotiationModal: React.FC<NegotiationModalProps> = ({
    player, onClose, onNegotiate, onFinalize, step, message, isSubmitting,
    selectedOfferMultiplier, onOfferMultiplierChange,
    selectedWageMultiplier, onWageMultiplierChange,
    assistantManagerLevel = 1,
    blockStatus
}) => {
    if (!player) return null;

    return (
        <Modal isOpen={!!player} onClose={onClose}>
            <h2>Negotiate for {player.name}</h2>
            {blockStatus?.blocked && (
                <div style={{ padding: '12px', backgroundColor: 'rgba(255, 69, 69, 0.1)', border: '1px solid var(--negative-color)', borderRadius: '4px', marginBottom: '12px' }}>
                    <p style={{ margin: '0', color: 'var(--negative-color)', fontSize: '0.95em' }}>
                        <strong>⛔ Recruitment Blocked</strong><br />
                        This player is unavailable for recruitment until <strong>week {blockStatus.weeksRemaining}</strong> (in {blockStatus.weeksRemaining} weeks).
                        After 3 rejected negotiations at either the club or player level, a 4-week cooldown period begins.
                    </p>
                </div>
            )}
            {message && <p className={message.type === 'success' ? 'positive' : 'negative'}>{message.text}</p>}
            
            {step === 'initial' && (
                <>
                    <p>Submit a transfer offer to {player.originalTeamName}.</p>
                    <p>Market Value: £{player.askingPrice.toLocaleString()}</p>
                    <ul className="negotiation-options">
                        {negotiationOptions.map(({ label, multiplier }) => (
                            <li key={label}>
                                <button
                                    className={`button ${selectedOfferMultiplier === multiplier ? 'primary' : ''}`}
                                    onClick={() => onOfferMultiplierChange(multiplier)}
                                >
                                    <span>{label} ({`£${Math.round(player.askingPrice * multiplier).toLocaleString()}`})</span>
                                </button>
                            </li>
                        ))}
                    </ul>
                    <div style={{ marginTop: '16px', padding: '12px', backgroundColor: 'var(--primary-color)', borderLeft: '3px solid var(--accent-color)', fontSize: '0.9em', lineHeight: '1.6' }}>
                        <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>👨‍💼 Assistant Manager Review</div>
                        <div>{generateTransferReview(player, selectedOfferMultiplier, assistantManagerLevel)}</div>
                    </div>
                    <div className="modal-actions">
                        <button className="button" onClick={onClose} disabled={isSubmitting}>Cancel</button>
                        <button className="button primary" onClick={onNegotiate} disabled={isSubmitting || blockStatus?.blocked}>{isSubmitting ? 'Submitting...' : 'Submit Offer'}</button>
                    </div>
                </>
            )}

            {step === 'player_terms' && (
                <>
                    <p>Negotiate a weekly wage with the player.</p>
                    <p>Current Wage: £{player.wage.toLocaleString()}/wk</p>
                    <ul className="negotiation-options">
                        {negotiationOptions.map(({ label, multiplier }) => (
                            <li key={label}>
                                <button 
                                    className={`button ${selectedWageMultiplier === multiplier ? 'primary' : ''}`}
                                    onClick={() => onWageMultiplierChange(multiplier)}
                                >
                                    <span>{label} ({`£${Math.round(player.wage * multiplier).toLocaleString()}/wk`})</span>
                                </button>
                            </li>
                        ))}
                    </ul>
                    <div className="modal-actions">
                        <button className="button" onClick={onClose} disabled={isSubmitting}>Withdraw</button>
                        <button className="button primary" onClick={onFinalize} disabled={isSubmitting || blockStatus?.blocked}>{isSubmitting ? 'Submitting...' : 'Submit Contract'}</button>
                    </div>
                </>
            )}

            {step === 'complete' && (
                <div className="modal-actions">
                    <button className="button" onClick={onClose}>Close</button>
                </div>
            )}
        </Modal>
    );
};
